import { Stack } from '@mui/material';
import { GridEventLookup, GridInitialState } from '@mui/x-data-grid-premium';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { GridInitialStatePremium } from '@mui/x-data-grid-premium/models/gridStatePremium';
import {
  IconArrowBackUp,
  IconDeviceFloppy,
  IconRestore,
} from '@tabler/icons-react';
import { actionButtonSpacing } from 'constants/themeConstants';
import useAddDatagridBreadcrumb, {
  DATAGRID_BREADCRUMB_TYPES,
} from 'hooks/useAddDatagridBreadcrumb';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'store';
import { saveDatagridState } from 'store/slices/datagrid';
import { DatagridNames, apiRefProps } from 'types/datagrid';
import { FullOrIconButton } from 'ui-component/DataGrid/components/FullOrIconButton';

type DatagridConfigurationControlsProps = {
  apiRef: apiRefProps;
  gridName: DatagridNames;
  handleResetState: () => void;
};

const SUBSCRIBED_EVENT_NAMES = [
  'pageSizeChange' as keyof GridEventLookup,
  'columnOrderChange' as keyof GridEventLookup,
  'columnVisibilityModelChange' as keyof GridEventLookup,
  'columnWidthChange' as keyof GridEventLookup,
  'filterModelChange' as keyof GridEventLookup,
  'rowGroupingModelChange' as keyof GridEventLookup,
  'sortModelChange' as keyof GridEventLookup,
];

const getGridStateForSaving = (
  gridApi: GridApiPremium
): GridInitialStatePremium => {
  const state = gridApi.exportState();

  // overwrite the values we don't want to persist
  state.preferencePanel = { open: false };
  state.filter = {
    filterModel: {
      items: state?.filter?.filterModel?.items ?? [],
    },
  };

  // ensure there's a sorting key
  if (!state.sorting) {
    state.sorting = {
      sortModel: [],
    };
  }

  // remove empty flex key before saving
  for (const dimension of Object.values(state.columns?.dimensions || {})) {
    if ('flex' in dimension && dimension.flex === undefined) {
      delete dimension.flex;
    }
  }

  return state;
};

export function DatagridConfigurationControls({
  apiRef,
  gridName,
  handleResetState,
}: DatagridConfigurationControlsProps) {
  const dispatch = useDispatch();
  const persistedGridState = useSelector((state) => state.datagrid);
  const isRestoringRef = useRef<boolean>(false);
  const [cachedGridState, setCachedGridState] =
    useState<GridInitialState | null>(persistedGridState[gridName]);
  const [hasDiffFromInitialState, setHasDiffFromInitialState] =
    useState<boolean>(false);

  const { handleLogSavedState } = useAddDatagridBreadcrumb();

  const handleSave = useCallback(() => {
    if (apiRef?.current) {
      const gridState = getGridStateForSaving(apiRef.current);

      dispatch(saveDatagridState({ gridName, gridState }));
      setCachedGridState(gridState);

      handleLogSavedState({
        gridState,
        gridName,
        type: DATAGRID_BREADCRUMB_TYPES.USER_SAVED,
      });
    }
  }, [apiRef, dispatch, gridName, handleLogSavedState]);

  const handleCacheState = useCallback(() => {
    if (apiRef?.current) {
      const newState = getGridStateForSaving(apiRef.current);

      setCachedGridState(newState);
      setHasDiffFromInitialState(true);
    }
  }, [apiRef]);

  useEffect(() => {
    if (apiRef?.current?.subscribeEvent) {
      const unsubscribers = SUBSCRIBED_EVENT_NAMES.map((event) =>
        apiRef.current.subscribeEvent(event, (params) => {
          // Ignore event-driven cache updates while we are programmatically restoring state
          if (isRestoringRef.current) return;

          // check if this is a filterModelChange event and the only change is to the quickFilterValues
          if (event === 'filterModelChange') {
            if (
              !_.isEqual(
                _.get(persistedGridState, [gridName, 'filter', 'filterModel'], {
                  items: [],
                  linkOperator: 'and',
                }),
                _.omit(params, 'quickFilterValues')
              )
            ) {
              handleCacheState();
            }
          } else {
            handleCacheState();
          }
        })
      );
      return () => {
        unsubscribers.forEach((unsubscribe) => unsubscribe());
      };
    }
    return undefined;
  }, [
    apiRef?.current?.subscribeEvent,
    handleCacheState,
    gridName,
    persistedGridState,
  ]);

  const handleUndo = useCallback(() => {
    if (persistedGridState[gridName] !== null) {
      isRestoringRef.current = true;
      apiRef.current?.restoreState(
        persistedGridState[gridName] as GridInitialState
      );
      // After restoring, sync the cached state and clear the diff flag so buttons disable
      setCachedGridState(persistedGridState[gridName] as GridInitialState);
      setHasDiffFromInitialState(false);
      // Allow event-driven updates again on the next tick
      setTimeout(() => {
        isRestoringRef.current = false;
      }, 0);
    }
  }, [apiRef, persistedGridState, gridName]);

  const userHasGridChanges = useMemo(() => {
    if (cachedGridState === null) return false;
    if (persistedGridState[gridName]) {
      const cachedGridStateCopy = _.cloneDeep(cachedGridState);
      // sometimes the `flex` key exists but is undefined, which causes the isEqual check to fail when compared to the persisted state
      for (const dimension of Object.values(
        cachedGridStateCopy.columns?.dimensions || {}
      )) {
        if ('flex' in dimension && dimension.flex === undefined) {
          delete dimension.flex;
        }
      }
      return !_.isEqual(cachedGridStateCopy, persistedGridState[gridName]);
    }
    return hasDiffFromInitialState;
  }, [cachedGridState, gridName, hasDiffFromInitialState, persistedGridState]);

  return (
    <Stack
      direction="row"
      justifyContent="flex-start"
      alignItems="center"
      spacing={actionButtonSpacing}
      sx={{ my: 1 }}
    >
      <FullOrIconButton
        disabled={!userHasGridChanges}
        onClick={handleSave}
        tooltip="Save current table settings"
        startIcon={<IconDeviceFloppy />}
        label="Save"
        color="primary"
        trackingName="Save Table Settings"
      />
      <FullOrIconButton
        startIcon={<IconArrowBackUp />}
        disabled={!userHasGridChanges}
        onClick={handleUndo}
        tooltip="Revert to last saved table settings"
        label="Reset"
        color="primary"
        trackingName="Revert to Last Saved Table Settings"
      />
      <FullOrIconButton
        onClick={() => {
          handleResetState();
          handleSave();
        }}
        tooltip="Revert to Cofactr default table settings"
        startIcon={<IconRestore />}
        label="Default"
        color="primary"
        trackingName="Revert to Cofactr Default Table Settings"
      />
    </Stack>
  );
}
